import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth/auth.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnDestroy {
  @ViewChild('settingsMenuRoot') settingsMenuRoot?: ElementRef<HTMLElement>;
  @ViewChild('workshopsMenuRoot') workshopsMenuRoot?: ElementRef<HTMLElement>;
  @ViewChild('towTrucksMenuRoot') towTrucksMenuRoot?: ElementRef<HTMLElement>;

  settingsMenuOpen = false;
  workshopsMenuOpen = false;
  towTrucksMenuOpen = false;
  mobileMenuOpen = false;

  private routerSub: Subscription;

  constructor(
    public auth: AuthService,
    private router: Router
  ) {
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.closeMobileMenu());
  }

  ngOnDestroy(): void {
    this.routerSub.unsubscribe();
    this.unlockBodyScroll();
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.syncBodyScroll();
  }

  closeMobileMenu(): void {
    if (!this.mobileMenuOpen) {
      return;
    }
    this.mobileMenuOpen = false;
    this.syncBodyScroll();
  }

  onDrawerClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a')) {
      this.closeMobileMenu();
    }
  }

  logout(): void {
    this.closeMobileMenu();
    this.auth.logout();
  }

  get showUsersMenu(): boolean {
    return this.auth.isSuperAdmin();
  }

  get showCompaniesMenu(): boolean {
    return this.auth.isSuperAdmin() || this.auth.isWorkshopOwner() || this.auth.isTowTruckOwner();
  }

  get showWorkshopsMenu(): boolean {
    return this.auth.isSuperAdmin() || this.auth.isWorkshopOwner();
  }

  get showFuelStationsMenu(): boolean {
    return this.auth.isSuperAdmin() || this.auth.isFuelStationOwner();
  }

  get showTowTrucksMenu(): boolean {
    return this.auth.isSuperAdmin() || this.auth.isTowTruckOwner();
  }

  get showAppUsersMenu(): boolean {
    return this.auth.isSuperAdmin();
  }

  get showSettingsMenu(): boolean {
    return this.auth.isSuperAdmin() || this.auth.isFuelStationOwner();
  }

  get showFinancialMenu(): boolean {
    return (
      this.auth.isSuperAdmin() ||
      this.auth.isWorkshopOwner() ||
      this.auth.isTowTruckOwner() ||
      this.auth.isFuelStationOwner()
    );
  }

  isSettingsSectionActive(): boolean {
    const u = this.router.url;
    return (
      u.startsWith('/admin/service-catalog') ||
      u.startsWith('/admin/payment-methods') ||
      u.startsWith('/admin/fuel-service-catalog')
    );
  }

  isWorkshopsSectionActive(): boolean {
    const u = this.router.url;
    return (
      u.startsWith('/admin/workshops') ||
      u.startsWith('/admin/workshop-leads') ||
      u.startsWith('/admin/workshop-services') ||
      u.startsWith('/admin/customer-vehicles') ||
      u.startsWith('/admin/appointments') ||
      u.startsWith('/admin/quote-requests')
    );
  }

  isFuelStationsSectionActive(): boolean {
    const u = this.router.url;
    return (
      u.startsWith('/admin/fuel-stations') ||
      u.startsWith('/admin/fuel-station-services') ||
      u.startsWith('/admin/fuel-station-payment-methods')
    );
  }

  isTowTrucksSectionActive(): boolean {
    const u = this.router.url;
    return (
      u.startsWith('/admin/tow-trucks') ||
      u.startsWith('/admin/tow-truck-calls')
    );
  }

  toggleSettingsMenu(event: Event): void {
    event.preventDefault();
    this.settingsMenuOpen = !this.settingsMenuOpen;
  }

  closeSettingsMenu(): void {
    this.settingsMenuOpen = false;
  }

  toggleWorkshopsMenu(event: Event): void {
    event.preventDefault();
    this.workshopsMenuOpen = !this.workshopsMenuOpen;
  }

  closeWorkshopsMenu(): void {
    this.workshopsMenuOpen = false;
  }

  toggleTowTrucksMenu(event: Event): void {
    event.preventDefault();
    this.towTrucksMenuOpen = !this.towTrucksMenuOpen;
  }

  closeTowTrucksMenu(): void {
    this.towTrucksMenuOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMobileMenu();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.closeMobileMenu();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    const t = ev.target as Node | null;

    if (this.settingsMenuOpen) {
      const root = this.settingsMenuRoot?.nativeElement;
      if (root && t && !root.contains(t)) this.settingsMenuOpen = false;
    }

    if (this.workshopsMenuOpen) {
      const root = this.workshopsMenuRoot?.nativeElement;
      if (root && t && !root.contains(t)) this.workshopsMenuOpen = false;
    }

    if (this.towTrucksMenuOpen) {
      const root = this.towTrucksMenuRoot?.nativeElement;
      if (root && t && !root.contains(t)) this.towTrucksMenuOpen = false;
    }
  }

  private syncBodyScroll(): void {
    document.body.classList.toggle('cms-nav-locked', this.mobileMenuOpen);
  }

  private unlockBodyScroll(): void {
    document.body.classList.remove('cms-nav-locked');
  }
}
